import { useEffect, useState } from "react";
import type { SnapshotSummary } from "@cs2/contracts";
import { History, RotateCcw } from "lucide-react";
import { activateSnapshot, listSnapshots } from "../api/client.js";
import { CloseButton, Dialog, useNotification } from "../ui/index.js";

function describe(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message.toUpperCase() : fallback;
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/**
 * Veröffentlichte Revisionen sind unveränderlich. Ein Rollback schreibt
 * deshalb keinen neuen Snapshot und lässt die Revisionshistorie unverändert.
 */
export function SnapshotsDialog({
  csrfToken,
  canRollback,
  onClose,
}: {
  csrfToken: string | null;
  canRollback: boolean;
  onClose: () => void;
}) {
  const { notify } = useNotification();
  const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([]);
  const [status, setStatus] = useState("LÄDT …");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listSnapshots()
      .then((loaded) => {
        if (!active) return;
        setSnapshots(loaded);
        setStatus(loaded.length ? "" : "NOCH NICHTS VERÖFFENTLICHT");
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus("");
        notify({
          message: describe(error, "HISTORIE NICHT LADBAR"),
          kind: "error",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  async function activate(revision: number) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await activateSnapshot(revision, csrfToken);
      setSnapshots((current) =>
        current.map((item) => ({
          ...item,
          active: item.revision === revision,
        })),
      );
      setStatus("");
      notify({
        message: `REVISION ${revision} IST JETZT AKTIV`,
        kind: "success",
      });
    } catch (error) {
      setStatus("");
      notify({
        message: describe(error, "UMSCHALTEN FEHLGESCHLAGEN"),
        kind: "error",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="VERÖFFENTLICHUNGEN"
      contentClassName="ui-modal-card snapshots-dialog"
    >
      <header>
        <h2>
          <History /> VERÖFFENTLICHUNGEN
        </h2>
        <CloseButton onClick={onClose} />
      </header>
      <table>
        <thead>
          <tr>
            <th>REV</th>
            <th>ZEITPUNKT</th>
            <th>TAKTIKEN</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {snapshots.map((snapshot) => (
            <tr
              key={snapshot.revision}
              className={snapshot.active ? "active" : ""}
            >
              <td>
                {snapshot.revision}
                {snapshot.active ? <em> · AKTIV</em> : null}
              </td>
              <td>{formatMoment(snapshot.publishedAt)}</td>
              <td>{snapshot.tacticCount}</td>
              <td>
                {snapshot.active || !canRollback ? null : (
                  <button
                    disabled={busy}
                    onClick={() => void activate(snapshot.revision)}
                  >
                    <RotateCcw /> AKTIVIEREN
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="members-status">{status}</p>
    </Dialog>
  );
}
